import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { postGuestbookMessage, fetchGuestbook, trackEvent } from './api';
import { EVENTS } from './events';
import { charleyMemoryConfig } from './config';
import bgImage from './assets/bg.png';
import { CmBackButton } from './CmBackButton';

/* ─── Animations ─── */
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Page & layout ─── */
const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  padding: 80px 20px 60px;
  box-sizing: border-box;
  background-image:
    linear-gradient(180deg, rgba(73, 79, 95, 0.72) 0%, rgba(7, 10, 18, 0.88) 100%),
    url(${bgImage});
  background-size: auto, cover;
  background-repeat: no-repeat;
  background-position: 0 0, 50% 100%;
  color: #e9ecf1;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  @media (max-width: 480px) {
    padding: 48px 12px 40px;
  }
`;

const ContentArea = styled.div`
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

/* ─── Header ─── */
const Header = styled.div`
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

const Title = styled.h1`
  font-size: 1.6rem;
  margin: 0 0 4px;
  color: #faf6e4;
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.65;
  font-size: 0.9rem;
`;

/* ─── Form ─── */
const FormCard = styled.form`
  background: rgba(12, 15, 25, 0.88);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} 0.45s ease;
  @media (max-width: 480px) {
    padding: 18px 16px;
  }
`;

const Label = styled.label`
  font-size: 0.82rem;
  opacity: 0.6;
  margin-bottom: -8px;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 10px 14px;
  color: #e9ecf1;
  font-size: 0.92rem;
  outline: none;
  transition: border-color 0.15s;
  &::placeholder { color: rgba(233, 236, 241, 0.35); }
  &:focus { border-color: rgba(250, 246, 228, 0.35); }
`;

const Textarea = styled.textarea`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 10px 14px;
  color: #e9ecf1;
  font-size: 0.92rem;
  outline: none;
  resize: vertical;
  min-height: 100px;
  max-height: 240px;
  font-family: inherit;
  line-height: 1.5;
  transition: border-color 0.15s;
  &::placeholder { color: rgba(233, 236, 241, 0.35); }
  &:focus { border-color: rgba(250, 246, 228, 0.35); }
`;

const SmallTextarea = styled(Textarea)`
  min-height: 60px;
  max-height: 160px;
`;

const SubmitBtn = styled.button`
  align-self: flex-end;
  border: none;
  border-radius: 999px;
  padding: 10px 28px;
  font-size: 0.95rem;
  cursor: pointer;
  background: #f8f1da;
  color: #24211c;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35);
  }
  &:disabled { opacity: 0.5; cursor: default; transform: none; box-shadow: none; }
`;

const SmallBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 6px 16px;
  font-size: 0.82rem;
  cursor: pointer;
  background: #f8f1da;
  color: #24211c;
  font-weight: 600;
  transition: transform 0.12s ease;
  &:hover { transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: default; transform: none; }
`;

const ErrorMsg = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(255, 99, 132, 0.08);
  border: 1px solid rgba(255, 99, 132, 0.3);
  color: #ffd8df;
  font-size: 0.85rem;
`;

const SuccessCard = styled.div`
  background: rgba(12, 15, 25, 0.88);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  animation: ${slideUp} 0.35s ease;
`;

const SuccessText = styled.div`
  color: #c8e6c0;
  font-size: 1rem;
  line-height: 1.8;
  margin-bottom: 20px;
`;

/* ─── Message list & cards ─── */
const MessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ${fadeIn} 0.5s ease 0.15s both;
`;

const MessageCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 16px 18px;
`;

const MessageAuthor = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  color: #faf6e4;
  margin-bottom: 6px;
`;

const MessageBody = styled.div`
  font-size: 0.9rem;
  opacity: 0.9;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const MessageFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const MessageTime = styled.span`
  font-size: 0.75rem;
  opacity: 0.45;
`;

const ReplyBtn = styled.button`
  border: none;
  background: none;
  color: rgba(250, 246, 228, 0.5);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  transition: color 0.15s;
  &:hover { color: rgba(250, 246, 228, 0.85); }
`;

/* ─── Replies ─── */
const RepliesWrap = styled.div`
  margin-top: 12px;
  padding-left: 14px;
  border-left: 2px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RepliesLabel = styled.span`
  font-size: 0.72rem;
  opacity: 0.4;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
`;

const ReplyCard = styled.div`
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const ReplyMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
`;

const ReplyAuthor = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(250, 246, 228, 0.9);
`;

const ReplyTime = styled.span`
  font-size: 0.7rem;
  opacity: 0.4;
`;

const ReplyBody = styled.div`
  font-size: 0.85rem;
  opacity: 0.9;
  line-height: 1.5;
  white-space: pre-wrap;
`;

/* ─── Inline reply form ─── */
const ReplyFormWrap = styled.form`
  margin-top: 12px;
  padding: 14px 16px;
  background: rgba(12, 15, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${fadeIn} 0.2s ease;
`;

const ReplyFormRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const CharCount = styled.span`
  font-size: 0.75rem;
  opacity: 0.45;
`;

/* ─── Helpers ─── */
function formatDate(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toLocaleString('zh-TW', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

function buildThreads(messages) {
  const topLevel = [];
  const byParent = new Map();
  for (const m of messages) {
    if (m.parent_id) {
      const arr = byParent.get(m.parent_id) || [];
      arr.push(m);
      byParent.set(m.parent_id, arr);
    } else {
      topLevel.push(m);
    }
  }
  for (const [, arr] of byParent) {
    arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }
  return { topLevel, byParent };
}

/* ─── Hooks ─── */
function useGuestbook(eventType) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackEvent(eventType);
    fetchGuestbook()
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventType]);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [msg, ...prev]);
  }, []);

  return { messages, loading, addMessage };
}

/* ─── Components ─── */
function InlineReplyForm({ parentId, onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const text = msg.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      const created = await postGuestbookMessage({ authorName: name.trim(), message: text, parentId });
      trackEvent(EVENTS.GUESTBOOK_REPLY, { parent_id: parentId });
      onCreated({
        id: created.id,
        parent_id: parentId,
        author_name: name.trim(),
        message: text,
        created_at: created.created_at,
      });
      setMsg('');
      setName('');
    } catch (err) {
      setError(err.message || '送出失敗');
    } finally {
      setSending(false);
    }
  }, [name, msg, parentId, onCreated]);

  return (
    <ReplyFormWrap onSubmit={handleSubmit}>
      <Input
        type="text"
        placeholder="你的名字（選填）"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
      />
      <SmallTextarea
        placeholder="回覆..."
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        maxLength={2000}
        required
      />
      {error && <ErrorMsg>{error}</ErrorMsg>}
      <ReplyFormRow>
        <ReplyBtn type="button" onClick={onCancel}>取消</ReplyBtn>
        <SmallBtn type="submit" disabled={sending || !msg.trim()}>
          {sending ? '送出中…' : '送出'}
        </SmallBtn>
      </ReplyFormRow>
    </ReplyFormWrap>
  );
}

function GuestbookContent({ messages, loading, addMessage, showTitle }) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);

  const { topLevel, byParent } = useMemo(() => buildThreads(messages), [messages]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const msg = message.trim();
    if (!msg) return;
    setSending(true);
    setError('');
    try {
      const created = await postGuestbookMessage({ authorName: name.trim(), message: msg });
      trackEvent(EVENTS.GUESTBOOK_SUBMIT, { name_length: name.trim().length, message_length: msg.length });
      setSent(true);
      addMessage({ id: created.id, author_name: name.trim(), message: msg, created_at: created.created_at });
      setMessage('');
      setName('');
    } catch (err) {
      setError(err.message || '送出失敗，請稍後再試');
    } finally {
      setSending(false);
    }
  }, [name, message, addMessage]);

  const handleReplyCreated = useCallback((reply) => {
    addMessage(reply);
    setReplyingTo(null);
  }, [addMessage]);

  return (
    <ContentArea>
      <Header>
        {showTitle && <Title>留言板</Title>}
        <Subtitle>留下你想說的話 :)</Subtitle>
      </Header>

      {sent ? (
        <SuccessCard>
          <SuccessText>謝謝你的留言！</SuccessText>
          <SubmitBtn type="button" onClick={() => setSent(false)}>再寫一則</SubmitBtn>
        </SuccessCard>
      ) : (
        <FormCard onSubmit={handleSubmit}>
          <Label>你的名字（選填）</Label>
          <Input
            type="text"
            placeholder="匿名也沒關係"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={100}
          />
          <Label>留言內容</Label>
          <Textarea
            placeholder="想說什麼..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={2000}
            required
          />
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <FormFooter>
            <CharCount>{message.length} / 2000</CharCount>
            <SubmitBtn type="submit" disabled={sending || !message.trim()}>
              {sending ? '送出中…' : '送出留言'}
            </SubmitBtn>
          </FormFooter>
        </FormCard>
      )}

      {loading ? null : topLevel.length > 0 && (
        <MessagesList>
          {topLevel.map((m) => {
            const replies = byParent.get(m.id) || [];
            return (
              <MessageCard key={m.id}>
                <MessageAuthor>{m.author_name || '匿名'}</MessageAuthor>
                <MessageBody>{m.message}</MessageBody>
                <MessageFooter>
                  <MessageTime>{formatDate(m.created_at)}</MessageTime>
                  <ReplyBtn type="button" onClick={() => setReplyingTo(replyingTo === m.id ? null : m.id)}>
                    回覆
                  </ReplyBtn>
                </MessageFooter>
                {replies.length > 0 && (
                  <RepliesWrap>
                    <RepliesLabel>共 {replies.length} 則回覆</RepliesLabel>
                    {replies.map((r) => (
                      <ReplyCard key={r.id}>
                        <ReplyMeta>
                          <ReplyAuthor>{r.author_name || '匿名'}</ReplyAuthor>
                          <ReplyTime>{formatDate(r.created_at)}</ReplyTime>
                        </ReplyMeta>
                        <ReplyBody>{r.message}</ReplyBody>
                      </ReplyCard>
                    ))}
                  </RepliesWrap>
                )}
                {replyingTo === m.id && (
                  <InlineReplyForm
                    parentId={m.id}
                    onCreated={handleReplyCreated}
                    onCancel={() => setReplyingTo(null)}
                  />
                )}
              </MessageCard>
            );
          })}
        </MessagesList>
      )}
    </ContentArea>
  );
}

/* ─── Standalone page ─── */
const GuestbookPage = () => {
  const navigate = useNavigate();
  const { messages, loading, addMessage } = useGuestbook(EVENTS.GUESTBOOK_PAGE_VIEW);

  return (
    <Page>
      <CmBackButton onClick={() => navigate(charleyMemoryConfig.routePath)} />
      <GuestbookContent messages={messages} loading={loading} addMessage={addMessage} showTitle />
    </Page>
  );
};

export default GuestbookPage;

/* ─── Overlay (fullscreen from main page) ─── */
const OverlayContainer = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #0a0c10;
  z-index: 1500;
  color: #e9ecf1;
  overflow: hidden;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const OverlayHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: rgba(7, 10, 18, 0.85);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const OverlayTitle = styled.span`
  font-weight: 600;
  font-size: 1rem;
`;

const OverlayScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 24px 16px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export function CharleyMemoryGuestbookView({ onBack }) {
  const { messages, loading, addMessage } = useGuestbook(EVENTS.GUESTBOOK_VIEW);

  return (
    <OverlayContainer>
      <OverlayHeader>
        <CmBackButton variant="header" onClick={onBack} />
        <OverlayTitle>留言板</OverlayTitle>
        <div style={{ width: 70 }} />
      </OverlayHeader>
      <OverlayScrollArea>
        <GuestbookContent messages={messages} loading={loading} addMessage={addMessage} />
      </OverlayScrollArea>
    </OverlayContainer>
  );
}
